import React from "react";
import styled, { useTheme } from "styled-components/native";
import {
  Dimensions,
  Modal,
  Platform,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBText } from "..";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SpaceType } from "../Spacer/types";
import { VDBIcons } from "@utils/VDBIcons";
import {
  KeyboardProvider,
  useReanimatedKeyboardAnimation,
} from "react-native-keyboard-controller";
import Animated, {
  withTiming,
  useSharedValue,
  useAnimatedStyle,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { DefaultTheme } from "styled-components/native";

type KeyboardAwareSlideSheetProps = {
  headerTitle: string;
  children: React.ReactNode;
  isOpen: boolean;
  onClose?: () => void;
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  minModalHeight?: number;
  backgroundColor?: string;
};

/**
 * A bottom sheet that keeps its content above the on-screen keyboard.
 *
 * This is a dedicated, self-contained sheet used by forms that live inside a
 * modal (e.g. the collection name modal). It is intentionally separate from the
 * shared `SlideSheet` so it can own the keyboard-avoidance behavior without
 * affecting the many other places that use `SlideSheet`.
 *
 * How it works: React Native's `Modal` renders in a separate native window, so
 * the app's root `KeyboardProvider` does not reach inside it. We re-provide the
 * `KeyboardProvider` here and lift the whole sheet above the keyboard by adding
 * the keyboard height as bottom padding on the overlay.
 */
const KeyboardAwareSlideSheet = (props: KeyboardAwareSlideSheetProps) => {
  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={props.isOpen}
      supportedOrientations={["portrait", "landscape"]}
    >
      <KeyboardProvider>
        <SheetContent {...props} />
      </KeyboardProvider>
    </Modal>
  );
};

const SheetContent = ({
  headerTitle,
  children,
  isOpen,
  onClose,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  minModalHeight,
  backgroundColor,
}: KeyboardAwareSlideSheetProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const userInfo = useUserInfo();
  const windowHeight = Dimensions.get("window").height;
  const insets = useSafeAreaInsets();
  const bottomInset = insets.bottom;

  const opacity = useSharedValue(isOpen ? 1 : 0);
  const translateY = useSharedValue(isOpen ? 0 : windowHeight);
  // `height` is negative while the keyboard is open (e.g. -300).
  const { height: keyboardHeight } = useReanimatedKeyboardAnimation();

  React.useEffect(() => {
    opacity.value = withTiming(isOpen ? 1 : 0, { duration: 300 });
    translateY.value = withTiming(isOpen ? 0 : windowHeight, { duration: 300 });
  }, [isOpen]);

  const backgroundStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const overlayStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    // Lift the flex-end sheet above the keyboard. The keyboard height is
    // measured from the bottom edge of the screen (including the bottom
    // safe-area region), so we subtract that inset to avoid a gap. Clamped to 0
    // so it has no effect while the keyboard is closed.
    paddingBottom:
      Platform.OS === "web"
        ? 0
        : Math.max(-keyboardHeight.value - bottomInset, 0),
  }));

  return (
    <>
      <BackgroundAnimated
        style={backgroundStyle}
        iFrame={userInfo.isIFrame()}
      />
      <OverlayAnimated style={overlayStyle}>
        <ModalBox
          backgroundColor={backgroundColor}
          windowHeight={windowHeight}
          minModalHeight={minModalHeight}
        >
          <OverlapContainer>
            <Header>
              <VDBText
                textAlign="center"
                type={typography["H2-M"]}
                color={theme["core-05"]}
              >
                {headerTitle}
              </VDBText>
            </Header>

            <ButtonContainer>
              <TouchableOpacity onPress={() => onClose?.()}>
                <VDBIcon
                  name={VDBIcons.CLOSE_CIRCLE}
                  variant={IconsVariant.VDBCustom}
                  size={24}
                  color={theme["core-06"]}
                />
              </TouchableOpacity>
            </ButtonContainer>
          </OverlapContainer>

          <LineView />
          <ScrollViewContainer
            paddingLeft={paddingLeft}
            paddingRight={paddingRight}
            paddingBottom={paddingBottom}
            paddingTop={paddingTop}
          >
            <ScrollView
              keyboardShouldPersistTaps="always"
              automaticallyAdjustKeyboardInsets={false}
              contentInsetAdjustmentBehavior="never"
            >
              {children}
            </ScrollView>
          </ScrollViewContainer>
        </ModalBox>
      </OverlayAnimated>
    </>
  );
};

const OverlayAnimated = styled(Animated.View)`
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
`;

const BackgroundAnimated = styled(Animated.View)<{ iFrame?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ModalBox = styled.View<{
  backgroundColor?: string;
  windowHeight?: number;
  minModalHeight?: number;
}>`
  background-color: ${({
    theme,
    backgroundColor,
  }: {
    theme: DefaultTheme;
    backgroundColor?: string;
  }) => (backgroundColor ? backgroundColor : theme["core-01"])};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  min-height: ${({ minModalHeight }: { minModalHeight?: number }) =>
    minModalHeight ? `${minModalHeight}px` : "auto"};
  max-height: ${({ windowHeight }: { windowHeight?: number }) =>
    windowHeight ? `${windowHeight * 0.9}px` : "90%"};
`;

const OverlapContainer = styled.View`
  position: relative;
  flex-direction: row;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`;

const Header = styled.View<any>`
  flex: 1;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  padding-top: ${({ theme }: { theme: DefaultTheme }) =>
    SpaceType.Double +
    (Platform.OS === "ios" && (theme.isDesktop || theme.isTablet) ? 2 : 0)}px;
  padding-bottom: ${({ theme }: { theme: DefaultTheme }) =>
    SpaceType.Double +
    (Platform.OS === "ios" && (theme.isDesktop || theme.isTablet) ? 2 : 0)}px;
`;

const ButtonContainer = styled.View`
  position: absolute;
  top: 1px;
  right: 2px;
  margin: ${SpaceType.Extra}px;
`;

const LineView = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const ScrollViewContainer = styled.View<{
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
}>`
  flex: 1;
  flex-basis: auto;
  padding-left: ${({ paddingLeft }: { paddingLeft?: number }) =>
    paddingLeft ? `${paddingLeft}px` : "0px"};
  padding-right: ${({ paddingRight }: { paddingRight?: number }) =>
    paddingRight ? `${paddingRight}px` : "0px"};
  padding-top: ${({ paddingTop }: { paddingTop?: number }) =>
    paddingTop ? `${paddingTop}px` : "0px"};
  padding-bottom: ${({ paddingBottom }: { paddingBottom?: number }) =>
    paddingBottom ? `${paddingBottom}px` : "0px"};
`;

export default KeyboardAwareSlideSheet;
